import React, { forwardRef, useEffect, useState } from 'react';
import { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import {
    DatePickerStyled,
    Calendar
} from '../HBDatepicker/HBDatepicker.styles';
import { IDatePicker } from '../HBDatepicker/HBDatepicker.types';
import CustomHeader from '../HBDatepicker/CustomHeader';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import HBDatepickerBase from './HBDatepickerBase';
import styled from 'styled-components';

export type HBDatepickerProps = IDatePicker &
    MustIncludeOneOfBasicTestIdWithIdProps;

const HBDatepicker = forwardRef<any, HBDatepickerProps>((props, ref) => {
    const {
        id,
        testIdName,
        testId,
        icon,
        customTheme,
        className,
        hasError,
        decreaseMonthOnClick,
        increaseMonthOnClick,
        datepickerType = 'day',
        onChange
    } = props;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'wrapper',
        componentName: 'HBDatepicker'
    });

    const [viewType, setViewType] = useState(datepickerType);

    useEffect(() => {
        setViewType(datepickerType);
    }, [datepickerType]);

    const datepickerTypeProp = {
        showMonthYearPicker: viewType === 'month',
        showYearPicker: viewType === 'year'
    };

    const renderCustomHeader = ({
        decreaseMonth,
        increaseMonth,
        decreaseYear,
        increaseYear,
        date,
        ...rest
    }: ReactDatePickerCustomHeaderProps) => {
        const decrease = () => {
            switch (viewType) {
                case 'day':
                    decreaseMonth();
                    break;
                case 'month':
                    decreaseYear();
                    break;
                case 'year':
                    decreaseYear();
                    break;
                default:
                    // eslint-disable-next-line no-console
                    console.error('unknown viewType');
                    decreaseMonth();
            }
        };

        const increase = () => {
            switch (viewType) {
                case 'day':
                    increaseMonth();
                    break;
                case 'month':
                    increaseYear();
                    break;
                case 'year':
                    increaseYear();
                    break;
                default:
                    // eslint-disable-next-line no-console
                    console.error('unknown viewType');
                    increaseMonth();
            }
        };
        return (
            <CustomHeader
                decrease={decrease}
                onDecreaseClick={decreaseMonthOnClick}
                onIncreaseClick={increaseMonthOnClick}
                increase={increase}
                date={date}
                viewType={viewType}
                setViewType={setViewType}
                {...rest}
            />
        );
    };

    const handleDateChange = (
        date: Date | null,
        event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ) => {
        if (viewType === 'year') {
            setViewType('month');
        }
        if (viewType === 'month' && datepickerType !== 'month') {
            setViewType('day');
        }
        onChange(date, event);
    };

    const onClickOutside = () => {
        setViewType(datepickerType);
    };

    return (
        <Wrapper
            customTheme={customTheme}
            className={className}
            hasError={hasError}
            data-testid={finalTestId}
        >
            <HBDatepickerBase
                ref={ref}
                {...props}
                {...datepickerTypeProp}
                shouldCloseOnSelect={viewType === datepickerType}
                onChange={handleDateChange}
                renderCustomHeader={renderCustomHeader}
                onClickOutside={onClickOutside}
            />
            {icon && <Calendar htmlFor={id}>{icon}</Calendar>}
        </Wrapper>
    );
});

export default HBDatepicker;

const Wrapper = styled(DatePickerStyled)``;
